<template>
  <section class="dp-layout fe-edu-page">
    <!-- 左侧机构树 -->
    <section class="dp-left">
      <DepartmentPane directory-scope="school" @tree-node-click="onTreeNodeClick" :type="1" />
    </section>

    <!-- 右侧内容 -->
    <section class="dp-right fe-main">
      <div v-if="!schoolId" class="fe-empty-state">
        <div class="fe-empty-state__icon">
          <i class="el-icon-school"></i>
        </div>
        <h3 class="fe-empty-state__title">选择学校开始配置</h3>
        <p class="fe-empty-state__desc">在左侧机构树中选择学校，即可管理五育评价成绩项</p>
      </div>

      <template v-else>
        <!-- 页头 -->
        <header class="fe-page-header">
          <div class="fe-page-header__left">
            <div class="fe-page-header__icon">
              <i class="el-icon-s-grid"></i>
            </div>
            <div class="fe-page-header__text">
              <h2 class="fe-page-header__title">成绩项管理</h2>
              <p class="fe-page-header__sub">{{ currentSchoolName }}</p>
            </div>
          </div>
          <el-button
            type="primary"
            size="small"
            icon="el-icon-plus"
            class="fe-btn-primary"
            @click="openL1Dialog()"
          >新增一级类别</el-button>
        </header>

        <!-- 年级筛选 -->
        <div v-if="gradeList.length > 0" class="fe-grade-bar">
          <span class="fe-grade-bar__label">适用年级</span>
          <div class="fe-grade-pills">
            <button
              type="button"
              class="fe-grade-pill"
              :class="{ 'fe-grade-pill--active': selectedGradeId === 0 }"
              @click="onGradeTabChange(0)"
            >全部</button>
            <button
              v-for="g in gradeList"
              :key="g.Id"
              type="button"
              class="fe-grade-pill"
              :class="{ 'fe-grade-pill--active': selectedGradeId === g.Id }"
              @click="onGradeTabChange(g.Id)"
            >{{ g.Name }}</button>
          </div>
        </div>

        <!-- 三列面板 -->
        <div class="fe-panels" v-loading="loading">
          <!-- 一级类别 -->
          <section class="fe-panel fe-panel--l1">
            <header class="fe-panel__head">
              <span class="fe-panel__level fe-panel__level--l1">L1</span>
              <span class="fe-panel__title">一级类别</span>
              <span class="fe-panel__count">{{ l1List.length }}</span>
            </header>
            <div class="fe-panel__body fe-panel__body--scroll">
              <div
                v-for="l1 in l1List"
                :key="l1.Id"
                class="fe-row"
                :class="{ 'fe-row--active': selectedL1 && selectedL1.Id === l1.Id }"
                @click="selectL1(l1)"
              >
                <div class="fe-row__main">
                  <span class="fe-row__name">{{ l1.Name }}</span>
                  <span
                    class="fe-row__tag"
                    :class="l1.IsEvaluatedByHomeroomTeacher ? 'fe-row__tag--warm' : 'fe-row__tag--cool'"
                  >{{ l1.IsEvaluatedByHomeroomTeacher ? '班主任' : '任教' }}</span>
                </div>
                <div class="fe-row__actions">
                  <el-button type="text" size="mini" icon="el-icon-edit" @click.stop="openL1Dialog(l1)" />
                  <el-button type="text" size="mini" icon="el-icon-delete" class="fe-btn-danger" @click.stop="handleDeleteL1(l1)" />
                </div>
              </div>
              <div v-if="l1List.length === 0" class="fe-panel-empty">
                <i class="el-icon-folder-opened"></i>
                <span>暂无一级类别</span>
              </div>
            </div>
            <footer class="fe-panel__foot">
              <el-button type="text" size="small" icon="el-icon-plus" class="fe-foot-btn" @click="openL1Dialog()">新增一级</el-button>
            </footer>
          </section>

          <!-- 二级类别 -->
          <section class="fe-panel fe-panel--l2">
            <header class="fe-panel__head">
              <span class="fe-panel__level fe-panel__level--l2">L2</span>
              <span class="fe-panel__title">二级类别</span>
              <span v-if="selectedL1" class="fe-panel__breadcrumb">{{ selectedL1.Name }}</span>
            </header>
            <div class="fe-panel__body fe-panel__body--scroll">
              <div v-if="!selectedL1" class="fe-panel-placeholder">
                <i class="el-icon-back"></i>
                <span>请先选择一级类别</span>
              </div>
              <template v-else>
                <div
                  v-for="l2 in l2List"
                  :key="l2.Id"
                  class="fe-row"
                  :class="{ 'fe-row--active': selectedL2 && selectedL2.Id === l2.Id }"
                  @click="selectL2(l2)"
                >
                  <div class="fe-row__main">
                    <span class="fe-row__name">{{ l2.Name }}</span>
                    <span v-if="l2.CourseName" class="fe-row__meta">{{ l2.CourseName }}</span>
                  </div>
                  <div class="fe-row__tail">
                    <span class="fe-row__badge">{{ l2.ItemCount }} 项</span>
                    <div class="fe-row__actions">
                      <el-button type="text" size="mini" icon="el-icon-edit" @click.stop="openL2Dialog(l2)" />
                      <el-button type="text" size="mini" icon="el-icon-delete" class="fe-btn-danger" @click.stop="handleDeleteL2(l2)" />
                    </div>
                  </div>
                </div>
                <div v-if="l2List.length === 0" class="fe-panel-empty">
                  <i class="el-icon-folder-add"></i>
                  <span>暂无二级类别</span>
                </div>
              </template>
            </div>
            <footer v-if="selectedL1" class="fe-panel__foot">
              <el-button type="text" size="small" icon="el-icon-plus" class="fe-foot-btn" @click="openL2Dialog()">新增二级</el-button>
            </footer>
          </section>

          <!-- 三级成绩项 -->
          <section class="fe-panel fe-panel--l3">
            <header class="fe-panel__head">
              <span class="fe-panel__level fe-panel__level--l3">L3</span>
              <span class="fe-panel__title">三级成绩项</span>
              <span v-if="selectedL2" class="fe-panel__breadcrumb">{{ selectedL2.Name }}</span>
            </header>
            <div class="fe-panel__body fe-panel__body--cards fe-panel__body--scroll">
              <div v-if="!selectedL2" class="fe-panel-placeholder">
                <i class="el-icon-back"></i>
                <span>请先选择二级类别</span>
              </div>
              <template v-else>
                <article
                  v-for="item in scoreItems"
                  :key="item.Id"
                  class="fe-score-card"
                >
                  <div class="fe-score-card__head">
                    <h4 class="fe-score-card__title">{{ item.Name }}</h4>
                    <div class="fe-score-card__actions">
                      <el-button type="text" size="mini" icon="el-icon-edit" @click="openItemDialog(item)" />
                      <el-button type="text" size="mini" icon="el-icon-delete" class="fe-btn-danger" @click="handleDeleteItem(item)" />
                    </div>
                  </div>
                  <div class="fe-score-card__meta">
                    <span class="fe-score-card__star">
                      <i class="el-icon-star-on"></i>
                      初始 {{ item.InitialStar }} / 满 {{ item.MaxStar }}
                    </span>
                    <span class="fe-score-card__grades">{{ formatGradeNames(item.GradeIds) }}</span>
                  </div>
                  <div v-if="item.StarStandards" class="fe-score-card__standards">
                    <span
                      v-for="n in 5"
                      :key="n"
                      v-show="parseStandard(item.StarStandards, n)"
                      class="fe-std-chip"
                    >{{ n }}★ {{ parseStandard(item.StarStandards, n) }}</span>
                  </div>
                </article>
                <div v-if="scoreItems.length === 0" class="fe-panel-empty">
                  <i class="el-icon-document-add"></i>
                  <span>暂无成绩项</span>
                </div>
              </template>
            </div>
            <footer v-if="selectedL2" class="fe-panel__foot fe-panel__foot--l3">
              <div class="fe-l3-foot__bar">
                <el-button
                  type="text"
                  size="small"
                  icon="el-icon-plus"
                  class="fe-foot-btn fe-l3-foot__add"
                  @click="openItemDialog()"
                >新增成绩项</el-button>
                <el-pagination
                  v-if="scoreTotalPages > 1"
                  class="fe-l3-pagination"
                  layout="prev, pager, next"
                  :current-page.sync="scorePageIndex"
                  :page-size="scorePageSize"
                  :total="scoreTotalCount"
                  @current-change="handleScorePageChange"
                />
                <span v-if="scoreTotalCount > 0" class="fe-l3-foot__total">共 {{ scoreTotalCount }} 项</span>
              </div>
            </footer>
          </section>
        </div>
      </template>
    </section>

    <!-- 一级类别弹窗 -->
    <el-dialog
      :title="l1Form.Id ? '编辑一级类别' : '新增一级类别'"
      :visible.sync="l1DialogVisible"
      width="480px"
      custom-class="fe-dialog"
      @close="resetL1Form"
    >
      <el-form :model="l1Form" :rules="l1Rules" ref="l1FormRef" label-width="120px" size="small">
        <el-form-item label="名称" prop="Name">
          <el-input v-model="l1Form.Name" placeholder="如：品德、学业、运动" maxlength="20" show-word-limit />
        </el-form-item>
        <el-form-item label="由班主任评价">
          <div class="fe-form-item-stack">
            <el-switch v-model="l1Form.IsEvaluatedByHomeroomTeacher" />
            <p class="fe-form-tip fe-form-tip--block">开启=班主任评价项，关闭=任教教师评价项</p>
          </div>
        </el-form-item>
        <el-form-item label="排序号">
          <el-input-number v-model="l1Form.SortOrder" :min="0" :max="999" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="l1DialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submitL1">保存</el-button>
      </div>
    </el-dialog>

    <!-- 二级类别弹窗 -->
    <el-dialog
      :title="l2Form.Id ? '编辑二级类别' : '新增二级类别'"
      :visible.sync="l2DialogVisible"
      width="480px"
      custom-class="fe-dialog"
      @close="resetL2Form"
    >
      <el-form :model="l2Form" :rules="l2Rules" ref="l2FormRef" label-width="120px" size="small">
        <el-form-item v-if="!isHomeroomScoreItem" label="关联科目" prop="CourseId">
          <el-select
            v-model="l2Form.CourseId"
            filterable
            clearable
            placeholder="请先选择科目"
            style="width: 100%"
            :loading="courseLoading"
            @change="onL2CourseChange"
          >
            <el-option
              v-for="item in courseList"
              :key="item.Id"
              :label="item.CourseName"
              :value="item.Id"
            />
          </el-select>
          <p class="fe-form-tip fe-form-tip--block">任教教师评价项须关联科目</p>
        </el-form-item>
        <el-form-item label="名称" prop="Name">
          <el-input
            v-model="l2Form.Name"
            :placeholder="isHomeroomScoreItem ? '如：班级表现' : '选择科目后自动填入，可修改'"
            maxlength="20"
            show-word-limit
          />
        </el-form-item>
        <el-form-item label="排序号">
          <el-input-number v-model="l2Form.SortOrder" :min="0" :max="999" />
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="l2DialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submitL2">保存</el-button>
      </div>
    </el-dialog>

    <!-- 三级成绩项弹窗 -->
    <el-dialog
      :title="itemForm.Id ? '编辑成绩项' : '新增成绩项'"
      :visible.sync="itemDialogVisible"
      width="600px"
      custom-class="fe-dialog fe-item-dialog"
      @close="resetItemForm"
    >
      <el-form :model="itemForm" :rules="itemRules" ref="itemFormRef" label-width="100px" size="small">
        <el-tabs v-model="itemDialogTab" class="fe-item-dialog__tabs">
          <el-tab-pane label="基本信息" name="basic">
            <el-form-item label="成绩项名称" prop="Name">
              <el-input v-model="itemForm.Name" placeholder="如：课堂表现" maxlength="50" show-word-limit />
            </el-form-item>
            <el-form-item label="初始星级" prop="InitialStar">
              <el-rate v-model="itemForm.InitialStar" :max="itemForm.MaxStar" />
              <p class="fe-form-tip fe-form-tip--block">教师进入评价页时的默认星级</p>
            </el-form-item>
            <el-form-item label="满星" prop="MaxStar">
              <el-rate v-model="itemForm.MaxStar" :max="5" @change="onMaxStarChange" />
              <p class="fe-form-tip fe-form-tip--block">该成绩项最高可评星级</p>
            </el-form-item>
            <el-form-item label="排序号">
              <el-input-number v-model="itemForm.SortOrder" :min="0" :max="999" />
            </el-form-item>
            <el-form-item label="适用年级">
              <el-select
                v-model="itemForm.gradeIdList"
                multiple
                collapse-tags
                placeholder="不选表示全部年级"
                style="width: 100%"
              >
                <el-option
                  v-for="g in gradeList"
                  :key="g.Id"
                  :label="g.Name"
                  :value="String(g.Id)"
                />
              </el-select>
              <p class="fe-form-tip fe-form-tip--block">不选择则对该校全部年级生效</p>
            </el-form-item>
            <el-form-item v-if="isHomeroomScoreItem" label="关联点评项">
              <el-cascader
                v-model="itemForm.commentIdList"
                :options="commentDimensionOptions"
                :props="commentDimensionCascaderProps"
                filterable
                clearable
                collapse-tags
                placeholder="请选择点评维度"
                style="width: 100%"
                :disabled="commentDimensionLoading"
                popper-class="fe-comment-cascader"
              />
              <p class="fe-form-tip fe-form-tip--block">班主任评价引用学生点评分时使用；可关联一级或二级维度，一级汇总该维度下全部点评，二级仅汇总对应子项</p>
            </el-form-item>
          </el-tab-pane>
          <el-tab-pane label="评价标准" name="standards">
            <div class="fe-standards-panel">
              <div class="fe-standards-panel__toolbar">
                <i class="el-icon-info fe-standards-panel__icon"></i>
                <span class="fe-standards-panel__hint">填写各档位说明，留空时教师端不展示</span>
                <el-button
                  size="mini"
                  plain
                  icon="el-icon-document-copy"
                  class="fe-standards-panel__fill-btn"
                  @click="applyAllStarStandardSamples"
                >填入参考文案</el-button>
              </div>
              <div
                v-for="n in itemForm.MaxStar"
                :key="n"
                class="fe-standard-card"
              >
                <div class="fe-standard-card__head">
                  <span class="fe-standard-card__badge">{{ n }} 星</span>
                  <el-rate :value="n" disabled :max="itemForm.MaxStar" />
                </div>
                <el-input
                  v-model="itemForm.standards[n]"
                  type="textarea"
                  :rows="2"
                  :placeholder="getStarStandardPlaceholder(n)"
                  maxlength="100"
                  class="fe-standard-card__input"
                />
              </div>
            </div>
          </el-tab-pane>
        </el-tabs>
      </el-form>
      <div slot="footer">
        <el-button @click="itemDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="submitItem">保存</el-button>
      </div>
    </el-dialog>
  </section>
</template>

<script>
import DepartmentPane from "@/components/DepartmentPane";
import { treeNodeDeptId } from '../../utils/deptCascaderProps';
import { getSubGrades } from '../../api/modules/department';
import { GetSysCourseByDepartmentId } from '../../api/modules/schedule-course';
import { getEvaluationDimensionSecondLevel } from '../../api/modules/moral-education';
import {
  getCategoryTree,
  saveCategoryL1,
  deleteCategoryL1,
  saveCategoryL2,
  deleteCategoryL2,
  getScoreItems,
  saveScoreItem,
  deleteScoreItem,
} from "@/api/fiveEducation";
import "./fe-theme.css";
import "./fe-theme-global.css";

/** 各星级评价标准默认 placeholder（课堂习惯示例） */
const defaultStarStandardPlaceholders = {
  1: "基本能参与课堂学习。",
  2: "主动参与课堂学习，认真倾听。",
  3: "积极参与课堂学习，认真倾听，紧跟课堂节奏有效互动。",
  4: "积极参与课堂学习，认真倾听，紧跟课堂节奏有效互动，乐于展示。",
  5: "积极参与课堂学习，认真倾听，紧跟课堂节奏有效互动，自信自然展现学习成果。",
};

export default {
  name: "FiveEducationScoreItemManage",
  components: { DepartmentPane },
  data() {
    return {
      /** 当前选中学校 ID */
      schoolId: 0,
      /** 当前选中学校名称 */
      currentSchoolName: "",
      loading: false,
      saving: false,

      /** 一级类别列表 */
      l1List: [],
      /** 当前选中一级 */
      selectedL1: null,
      /** 二级类别列表（由选中一级决定） */
      l2List: [],
      /** 当前选中二级 */
      selectedL2: null,
      /** 三级成绩项列表 */
      scoreItems: [],
      /** 三级成绩项总数 */
      scoreTotalCount: 0,
      /** 三级成绩项分页 */
      scorePageIndex: 1,
      scorePageSize: 10,

      /** 学校下年级列表 */
      gradeList: [],
      /** 当前筛选年级（0=全部） */
      selectedGradeId: 0,

      /** 学校下科目列表 */
      courseList: [],
      courseLoading: false,

      // ── 一级弹窗 ──
      l1DialogVisible: false,
      l1Form: { Id: null, Name: "", IsEvaluatedByHomeroomTeacher: false, SortOrder: 0 },
      l1Rules: {
        Name: [{ required: true, message: "请填写名称", trigger: "blur" }],
      },

      // ── 二级弹窗 ──
      l2DialogVisible: false,
      l2Form: { Id: null, Name: "", CourseId: null, SortOrder: 0, ParentId: 0 },

      /** 学校下点评维度树（一/二级） */
      commentDimensionTree: [],
      commentDimensionLoading: false,

      // ── 三级弹窗 ──
      itemDialogVisible: false,
      itemDialogTab: "basic",
      itemForm: {
        Id: null,
        Name: "",
        InitialStar: 5,
        MaxStar: 5,
        SortOrder: 0,
        commentIdList: [],
        gradeIdList: [],
        standards: { 1: "", 2: "", 3: "", 4: "", 5: "" },
      },
      itemRules: {
        Name: [{ required: true, message: "请填写成绩项名称", trigger: "blur" }],
        InitialStar: [{ required: true, message: "请设置初始星级", trigger: "change" }],
        MaxStar: [{ required: true, message: "请设置满星", trigger: "change" }],
      },
    };
  },
  computed: {
    /** 当前一级是否为班主任评价（任教项无需关联点评维度） */
    isHomeroomScoreItem() {
      return !!(this.selectedL1 && this.selectedL1.IsEvaluatedByHomeroomTeacher);
    },
    /** 二级类别表单校验（任教项须选科目） */
    l2Rules() {
      const rules = {
        Name: [{ required: true, message: "请填写名称", trigger: ["blur", "change"] }],
      };
      if (this.selectedL1 && !this.selectedL1.IsEvaluatedByHomeroomTeacher) {
        rules.CourseId = [{ required: true, message: "请选择关联科目", trigger: "change" }];
      }
      return rules;
    },
    /** 点评维度级联选项 */
    commentDimensionOptions() {
      return (this.commentDimensionTree || []).map(function (parent) {
        const node = {
          value: String(parent.Id),
          label: parent.Name,
        };
        const children = (parent.Children || []).map(function (child) {
          return {
            value: String(child.Id),
            label: child.Name,
          };
        });
        if (children.length) {
          node.children = children;
        }
        return node;
      });
    },
    /** 点评维度级联配置：左右分栏多选，一级/二级均可独立勾选 */
    commentDimensionCascaderProps() {
      return {
        multiple: true,
        checkStrictly: true,
        emitPath: false,
        expandTrigger: "hover",
      };
    },
    /** 三级成绩项总页数 */
    scoreTotalPages() {
      if (!this.scoreTotalCount) return 0;
      return Math.ceil(this.scoreTotalCount / this.scorePageSize);
    },
  },
  methods: {
    /** 左侧机构树节点点击 */
    onTreeNodeClick(node) {
      const data = node && node.data ? node.data : node;
      if (!data) return;
      const orgType = String(
        data.DeptOrgType != null ? data.DeptOrgType : data.deptOrgType || ""
      ).toLowerCase();
      const isSchool = orgType === "school" || orgType === "affiliateschool";
      if (!isSchool) return;
      this.schoolId = treeNodeDeptId(data);
      this.currentSchoolName = data.label || data.Name || "";
      this.selectedL1 = null;
      this.selectedL2 = null;
      this.l2List = [];
      this.scoreItems = [];
      this.scoreTotalCount = 0;
      this.selectedGradeId = 0;
      this.courseList = [];
      this.commentDimensionTree = [];
      this.loadGradeList();
      this.loadCategoryTree();
    },

    /** 加载学校下科目列表 */
    async loadCourseList() {
      this.courseList = [];
      if (!this.schoolId) return;
      this.courseLoading = true;
      try {
        const res = await GetSysCourseByDepartmentId({ departmentId: this.schoolId });
        if (res.data && res.data.response) {
          this.courseList = res.data.response;
        }
      } catch (e) {
        this.$message.error("加载科目列表失败");
      } finally {
        this.courseLoading = false;
      }
    },

    /** 加载学校下点评维度（一/二级） */
    async loadCommentDimensionOptions() {
      this.commentDimensionTree = [];
      if (!this.schoolId) return;
      this.commentDimensionLoading = true;
      try {
        const res = await getEvaluationDimensionSecondLevel({ departmentId: this.schoolId });
        if (res.data && res.data.success) {
          this.commentDimensionTree = res.data.response || [];
        }
      } catch (e) {
        this.$message.error("加载点评维度失败");
      } finally {
        this.commentDimensionLoading = false;
      }
    },

    /** 加载学校下年级列表 */
    loadGradeList() {
      this.gradeList = [];
      if (!this.schoolId) return;
      getSubGrades({ pid: this.schoolId }).then((res) => {
        if (res.data && res.data.response) {
          this.gradeList = res.data.response;
        }
      });
    },

    /** 切换年级筛选 */
    async onGradeTabChange(gradeId) {
      if (this.selectedGradeId === gradeId) return;
      this.selectedGradeId = gradeId;
      this.selectedL2 = null;
      this.scoreItems = [];
      this.scorePageIndex = 1;
      await this.loadCategoryTree();
    },

    /** 加载一级类别树 */
    async loadCategoryTree() {
      if (!this.schoolId) return;
      this.loading = true;
      try {
        const res = await getCategoryTree(this.schoolId, this.selectedGradeId);
        if (res.data && res.data.success) {
          this.l1List = res.data.response || [];
          await this.applyDefaultSelection();
        } else {
          this.$message.error((res.data && res.data.msg) || "加载失败");
        }
      } catch (e) {
        this.$message.error("网络错误");
      } finally {
        this.loading = false;
      }
    },

    /** 树加载后默认选中第一项（尽量保持原选中项） */
    async applyDefaultSelection() {
      if (!this.l1List.length) {
        this.selectedL1 = null;
        this.selectedL2 = null;
        this.l2List = [];
        this.scoreItems = [];
        return;
      }

      const prevL1Id = this.selectedL1 ? this.selectedL1.Id : null;
      const prevL2Id = this.selectedL2 ? this.selectedL2.Id : null;
      const l1 = this.l1List.find((l) => l.Id === prevL1Id) || this.l1List[0];

      this.selectedL1 = l1;
      this.l2List = l1.Children || [];

      if (!this.l2List.length) {
        this.selectedL2 = null;
        this.scoreItems = [];
        return;
      }

      const l2 = this.l2List.find((l) => l.Id === prevL2Id) || this.l2List[0];
      await this.selectL2(l2);
    },

    /** 选中一级，展示二级 */
    async selectL1(l1) {
      this.selectedL1 = l1;
      this.selectedL2 = null;
      this.scoreItems = [];
      this.scorePageIndex = 1;
      this.l2List = l1.Children || [];
      if (this.l2List.length) {
        await this.selectL2(this.l2List[0]);
      }
    },

    /** 选中二级，加载三级成绩项 */
    async selectL2(l2) {
      this.selectedL2 = l2;
      this.scoreItems = [];
      this.scorePageIndex = 1;
      await this.loadScoreItems(l2.Id);
    },

    /** 加载三级成绩项（接口分页） */
    async loadScoreItems(categoryL2Id, page) {
      const l2Id = categoryL2Id || (this.selectedL2 && this.selectedL2.Id);
      if (!l2Id) return;
      if (page) this.scorePageIndex = page;
      this.loading = true;
      try {
        const res = await getScoreItems(
          l2Id,
          this.selectedGradeId,
          this.scorePageIndex,
          this.scorePageSize
        );
        if (res.data && res.data.success) {
          const pageModel = res.data.response || {};
          this.scoreItems = pageModel.data || [];
          this.scoreTotalCount = pageModel.dataCount != null ? pageModel.dataCount : 0;
          if (pageModel.page) this.scorePageIndex = pageModel.page;
          if (this.scorePageIndex > 1 && this.scoreItems.length === 0 && this.scoreTotalCount > 0) {
            this.scorePageIndex -= 1;
            await this.loadScoreItems(l2Id);
          }
        } else {
          this.scoreItems = [];
          this.scoreTotalCount = 0;
        }
      } catch (e) {
        this.$message.error("加载成绩项失败");
      } finally {
        this.loading = false;
      }
    },

    /** 三级成绩项翻页 */
    handleScorePageChange(page) {
      this.scorePageIndex = page;
      this.loadScoreItems();
    },

    // ── 一级弹窗 ──

    openL1Dialog(l1) {
      this.l1DialogVisible = true;
      if (l1) {
        this.l1Form = {
          Id: l1.Id,
          Name: l1.Name,
          IsEvaluatedByHomeroomTeacher: !!l1.IsEvaluatedByHomeroomTeacher,
          SortOrder: l1.SortOrder || 0,
          DepartmentId: this.schoolId,
        };
      } else {
        this.l1Form = {
          Id: null,
          Name: "",
          IsEvaluatedByHomeroomTeacher: false,
          SortOrder: 0,
          DepartmentId: this.schoolId,
        };
      }
    },

    resetL1Form() {
      this.$refs.l1FormRef && this.$refs.l1FormRef.resetFields();
    },

    async submitL1() {
      this.$refs.l1FormRef.validate(async (valid) => {
        if (!valid) return;
        this.saving = true;
        try {
          const res = await saveCategoryL1(this.l1Form);
          if (res.data && res.data.success) {
            this.$message.success("保存成功");
            this.l1DialogVisible = false;
            await this.loadCategoryTree();
          } else {
            this.$message.error((res.data && res.data.msg) || "保存失败");
          }
        } catch (e) {
          this.$message.error("网络错误");
        } finally {
          this.saving = false;
        }
      });
    },

    async handleDeleteL1(l1) {
      await this.$confirm(
        `确定删除一级类别「${l1.Name}」？删除前须先移除其下所有二级类别。`,
        "删除确认",
        { type: "warning" }
      );
      try {
        const res = await deleteCategoryL1(l1.Id);
        if (res.data && res.data.success) {
          this.$message.success("删除成功");
          if (this.selectedL1 && this.selectedL1.Id === l1.Id) {
            this.selectedL1 = null;
            this.l2List = [];
          }
          await this.loadCategoryTree();
        } else {
          this.$message.error((res.data && res.data.msg) || "删除失败");
        }
      } catch (e) {
        if (e !== "cancel") this.$message.error("操作失败");
      }
    },

    // ── 二级弹窗 ──

    openL2Dialog(l2) {
      if (!this.isHomeroomScoreItem) {
        this.loadCourseList();
      }
      if (l2) {
        Object.assign(this.l2Form, {
          Id: l2.Id,
          Name: l2.Name || "",
          CourseId: this.isHomeroomScoreItem ? null : (l2.CourseId || null),
          SortOrder: l2.SortOrder || 0,
          ParentId: this.selectedL1.Id,
        });
      } else {
        Object.assign(this.l2Form, {
          Id: null,
          Name: "",
          CourseId: null,
          SortOrder: 0,
          ParentId: this.selectedL1 ? this.selectedL1.Id : 0,
        });
      }
      this.l2DialogVisible = true;
      this.$nextTick(() => {
        if (this.$refs.l2FormRef) {
          this.$refs.l2FormRef.clearValidate();
        }
      });
    },

    /** 选择科目后自动填入名称（仍可手动修改） */
    onL2CourseChange(courseId) {
      if (!courseId) {
        this.l2Form.Name = "";
        return;
      }
      const course = this.courseList.find(function (c) {
        return String(c.Id) === String(courseId);
      });
      if (course && course.CourseName) {
        this.l2Form.Name = course.CourseName;
      }
      this.$nextTick(() => {
        if (this.$refs.l2FormRef) {
          this.$refs.l2FormRef.clearValidate("Name");
        }
      });
    },

    resetL2Form() {
      Object.assign(this.l2Form, {
        Id: null,
        Name: "",
        CourseId: null,
        SortOrder: 0,
        ParentId: 0,
      });
      this.$nextTick(() => {
        if (this.$refs.l2FormRef) {
          this.$refs.l2FormRef.clearValidate();
        }
      });
    },

    async submitL2() {
      this.$refs.l2FormRef.validate(async (valid) => {
        if (!valid) return;
        this.saving = true;
        try {
          const payload = Object.assign({}, this.l2Form, {
            CourseId: this.isHomeroomScoreItem ? null : this.l2Form.CourseId,
          });
          const res = await saveCategoryL2(payload);
          if (res.data && res.data.success) {
            this.$message.success("保存成功");
            this.l2DialogVisible = false;
            await this.loadCategoryTree();
            // 刷新当前选中一级的二级列表
            if (this.selectedL1) {
              const updated = this.l1List.find((l) => l.Id === this.selectedL1.Id);
              if (updated) {
                this.selectedL1 = updated;
                this.l2List = updated.Children || [];
              }
            }
          } else {
            this.$message.error((res.data && res.data.msg) || "保存失败");
          }
        } catch (e) {
          this.$message.error("网络错误");
        } finally {
          this.saving = false;
        }
      });
    },

    async handleDeleteL2(l2) {
      await this.$confirm(
        `确定删除二级类别「${l2.Name}」？删除前须先移除其下所有三级成绩项。`,
        "删除确认",
        { type: "warning" }
      );
      try {
        const res = await deleteCategoryL2(l2.Id);
        if (res.data && res.data.success) {
          this.$message.success("删除成功");
          if (this.selectedL2 && this.selectedL2.Id === l2.Id) {
            this.selectedL2 = null;
            this.scoreItems = [];
          }
          await this.loadCategoryTree();
          var updated = this.l1List.find(function(l) { return l.Id === (this.selectedL1 && this.selectedL1.Id); }.bind(this));
          if (updated) {
            this.selectedL1 = updated;
            this.l2List = updated.Children || [];
          }
        } else {
          this.$message.error((res.data && res.data.msg) || "删除失败");
        }
      } catch (e) {
        if (e !== "cancel") this.$message.error("操作失败");
      }
    },

    // ── 三级成绩项弹窗 ──

    openItemDialog(item) {
      this.itemDialogVisible = true;
      this.itemDialogTab = "basic";
      if (this.isHomeroomScoreItem) {
        this.loadCommentDimensionOptions();
      } else {
        this.commentDimensionTree = [];
      }
      if (item) {
        const standards = this.parseAllStandards(item.StarStandards);
        this.itemForm = {
          Id: item.Id,
          Name: item.Name,
          InitialStar: item.InitialStar || 5,
          MaxStar: item.MaxStar || 5,
          SortOrder: item.SortOrder || 0,
          commentIdList: this.isHomeroomScoreItem
            ? this.parseCommentIdList(item.CommentIds)
            : [],
          gradeIdList: this.parseGradeIdList(item.GradeIds),
          CategoryL2Id: this.selectedL2.Id,
          standards,
        };
      } else {
        this.itemForm = {
          Id: null,
          Name: "",
          InitialStar: 5,
          MaxStar: 5,
          SortOrder: 0,
          commentIdList: [],
          gradeIdList: this.selectedGradeId > 0 ? [String(this.selectedGradeId)] : [],
          CategoryL2Id: this.selectedL2 ? this.selectedL2.Id : 0,
          standards: { 1: "", 2: "", 3: "", 4: "", 5: "" },
        };
      }
    },

    /** 满星变更：限制初始星级并清理超出档位的标准 */
    onMaxStarChange(maxStar) {
      if (!maxStar) return;
      if (this.itemForm.InitialStar > maxStar) {
        this.itemForm.InitialStar = maxStar;
      }
      for (let n = maxStar + 1; n <= 5; n++) {
        this.itemForm.standards[n] = "";
      }
    },

    resetItemForm() {
      this.itemDialogTab = "basic";
      this.$refs.itemFormRef && this.$refs.itemFormRef.resetFields();
    },

    async submitItem() {
      this.$refs.itemFormRef.validate(async (valid) => {
        if (!valid) {
          this.itemDialogTab = "basic";
          return;
        }
        this.saving = true;
        try {
          // 将 standards 对象转成 JSON 字符串
          const starStandards = JSON.stringify(this.itemForm.standards);
          const payload = {
            Id: this.itemForm.Id,
            Name: this.itemForm.Name,
            CategoryL2Id: this.itemForm.CategoryL2Id,
            InitialStar: this.itemForm.InitialStar,
            MaxStar: this.itemForm.MaxStar,
            SortOrder: this.itemForm.SortOrder,
            CommentIds: this.isHomeroomScoreItem
              ? (this.itemForm.commentIdList || []).join(",")
              : "",
            StarStandards: starStandards,
            GradeIds: (this.itemForm.gradeIdList || []).map(function (id) {
              return String(id);
            }),
          };
          const res = await saveScoreItem(payload);
          if (res.data && res.data.success) {
            this.$message.success("保存成功");
            this.itemDialogVisible = false;
            await this.loadCategoryTree();
            if (this.selectedL1) {
              const updated = this.l1List.find((l) => l.Id === this.selectedL1.Id);
              if (updated) {
                this.selectedL1 = updated;
                this.l2List = updated.Children || [];
              }
            }
            await this.loadScoreItems(this.selectedL2.Id);
          } else {
            this.$message.error((res.data && res.data.msg) || "保存失败");
          }
        } catch (e) {
          this.$message.error("网络错误");
        } finally {
          this.saving = false;
        }
      });
    },

    async handleDeleteItem(item) {
      await this.$confirm(
        `确定删除成绩项「${item.Name}」？已有评星记录时无法删除。`,
        "删除确认",
        { type: "warning" }
      );
      try {
        const res = await deleteScoreItem(item.Id);
        if (res.data && res.data.success) {
          this.$message.success("删除成功");
          await this.loadCategoryTree();
          if (this.selectedL1) {
            const updated = this.l1List.find((l) => l.Id === this.selectedL1.Id);
            if (updated) {
              this.selectedL1 = updated;
              this.l2List = updated.Children || [];
            }
          }
          await this.loadScoreItems(this.selectedL2.Id);
        } else {
          this.$message.error((res.data && res.data.msg) || "删除失败");
        }
      } catch (e) {
        if (e !== "cancel") this.$message.error("操作失败");
      }
    },

    // ── 工具方法 ──

    /** 各星级评价标准 placeholder */
    getStarStandardPlaceholder(star) {
      return defaultStarStandardPlaceholders[star] || star + " 星对应的评价说明";
    },

    /** 将参考文案填入指定星级标准 */
    applyStarStandardSample(star) {
      this.$set(this.itemForm.standards, star, this.getStarStandardPlaceholder(star));
    },

    /** 将全部星级标准填入参考文案 */
    applyAllStarStandardSamples() {
      for (let n = 1; n <= this.itemForm.MaxStar; n++) {
        this.applyStarStandardSample(n);
      }
    },

    /** 将 CommentIds 字符串解析为 ID 数组 */
    parseCommentIdList(commentIds) {
      if (!commentIds) return [];
      return commentIds.split(",").map(function (s) { return s.trim(); }).filter(function (id) { return id && id !== "0"; });
    },

    /** 将 GradeIds 字符串解析为 ID 数组（保持字符串，避免大整数精度丢失） */
    parseGradeIdList(gradeIds) {
      if (!gradeIds) return [];
      return gradeIds.split(",").map(function (s) { return s.trim(); }).filter(function (id) { return id && id !== "0"; });
    },

    /** 根据年级 ID 取名称 */
    getGradeName(gradeId) {
      const g = this.gradeList.find(function (item) {
        return String(item.Id) === String(gradeId);
      });
      return g ? g.Name : "";
    },

    /** 格式化适用年级名称 */
    formatGradeNames(gradeIds) {
      if (!gradeIds) return "全部年级";
      const ids = this.parseGradeIdList(gradeIds);
      if (ids.length === 0) return "全部年级";
      const names = [];
      for (let i = 0; i < ids.length; i++) {
        const name = this.getGradeName(ids[i]);
        names.push(name || ids[i]);
      }
      return names.join("、");
    },

    /** 从 StarStandards JSON 字符串解析单个星级说明 */
    parseStandard(json, star) {
      try {
        const obj = JSON.parse(json || "{}");
        return obj[String(star)] || "";
      } catch (e) {
        return "";
      }
    },

    /** 将 StarStandards JSON 字符串解析为 {1:'', 2:'', ...} 对象 */
    parseAllStandards(json) {
      const defaults = { 1: "", 2: "", 3: "", 4: "", 5: "" };
      try {
        const obj = JSON.parse(json || "{}");
        return Object.assign(defaults, obj);
      } catch (e) {
        return defaults;
      }
    },
  },
};
</script>

<style scoped>
/* 成绩项管理 — 三列面板需固定高度 */
.fe-main { overflow: hidden; }

/* 年级筛选胶囊 */
.fe-grade-bar {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}
.fe-grade-bar__label {
  flex-shrink: 0;
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.fe-grade-pills {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  overflow-x: auto;
}
.fe-grade-pill {
  padding: 6px 14px;
  font-size: 13px;
  color: #475569;
  background: #f1f5f9;
  border: 1px solid transparent;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}
.fe-grade-pill:hover {
  background: #e2e8f0;
  color: #1e40af;
}
.fe-grade-pill--active {
  background: #eff6ff;
  color: #1e40af;
  border-color: #93c5fd;
  font-weight: 600;
  box-shadow: 0 1px 4px rgba(59, 130, 246, 0.15);
}

/* ── 三列面板 ── */
.fe-panels {
  flex: 1;
  display: flex;
  gap: 12px;
  min-height: 0;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}
.fe-panels::-webkit-scrollbar {
  height: 6px;
}
.fe-panels::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}
.fe-panels::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
.fe-panel {
  display: flex;
  flex-direction: column;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
  overflow: hidden;
  flex-shrink: 0;
  min-height: 0;
  max-height: 100%;
}
.fe-panel--l1 { flex: 0 0 240px; width: 240px; }
.fe-panel--l2 { flex: 0 0 280px; width: 280px; }
.fe-panel--l3 { flex: 1 0 360px; min-width: 360px; }

.fe-panel__head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  background: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
}
.fe-panel__level {
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  letter-spacing: 0.05em;
}
.fe-panel__level--l1 { background: #dbeafe; color: #1e40af; }
.fe-panel__level--l2 { background: #e0e7ff; color: #4338ca; }
.fe-panel__level--l3 { background: #fef3c7; color: #b45309; }
.fe-panel__title {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
}
.fe-panel__count {
  margin-left: auto;
  font-size: 12px;
  color: #94a3b8;
  background: #f1f5f9;
  padding: 2px 8px;
  border-radius: 10px;
}
.fe-panel__breadcrumb {
  margin-left: auto;
  font-size: 12px;
  color: #3b82f6;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.fe-panel__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 8px;
}
.fe-panel__body--scroll {
  -webkit-overflow-scrolling: touch;
}
.fe-panel__body--cards {
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.fe-panel__foot {
  padding: 8px 12px;
  border-top: 1px solid #e2e8f0;
  background: #fafbfc;
  flex-shrink: 0;
}
.fe-panel__foot--l3 {
  padding: 10px 12px;
}
.fe-l3-foot__bar {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 28px;
}
.fe-l3-foot__total {
  flex-shrink: 0;
  margin-left: auto;
  font-size: 12px;
  color: #94a3b8;
  white-space: nowrap;
}
.fe-l3-foot__add {
  flex-shrink: 0;
}
.fe-l3-pagination {
  flex: 1;
  min-width: 0;
  padding: 0 !important;
  text-align: center;
}
.fe-l3-pagination.el-pagination >>> .btn-prev,
.fe-l3-pagination.el-pagination >>> .btn-next {
  min-width: 28px;
  height: 28px;
  line-height: 28px;
  padding: 0;
  margin: 0 2px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  color: #475569;
}
.fe-l3-pagination.el-pagination >>> .btn-prev:hover:not(:disabled),
.fe-l3-pagination.el-pagination >>> .btn-next:hover:not(:disabled) {
  color: #2563eb;
  border-color: #93c5fd;
  background: #eff6ff;
}
.fe-l3-pagination.el-pagination >>> .btn-prev:disabled,
.fe-l3-pagination.el-pagination >>> .btn-next:disabled {
  color: #cbd5e1;
  background: #f8fafc;
}
.fe-l3-pagination.el-pagination >>> .el-pager {
  margin: 0 4px;
}
.fe-l3-pagination.el-pagination >>> .el-pager li {
  min-width: 28px;
  height: 28px;
  line-height: 28px;
  margin: 0 2px;
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 6px;
}
.fe-l3-pagination.el-pagination >>> .el-pager li:hover {
  color: #2563eb;
  background: #f8fafc;
}
.fe-l3-pagination.el-pagination >>> .el-pager li.active {
  color: #fff;
  background: linear-gradient(135deg, #1e40af 0%, #2563eb 100%);
  border-color: transparent;
  box-shadow: 0 1px 4px rgba(37, 99, 235, 0.25);
}
.fe-foot-btn { color: #2563eb !important; font-weight: 500; }

/* ── 列表行 ── */
.fe-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  margin-bottom: 4px;
  border-radius: 8px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.15s ease;
}
.fe-row:hover {
  background: #f8fafc;
  border-color: #e2e8f0;
}
.fe-row--active {
  background: #eff6ff;
  border-color: #93c5fd;
  box-shadow: inset 3px 0 0 #2563eb;
}
.fe-row__main {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
}
.fe-row__name {
  font-size: 13px;
  font-weight: 500;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.fe-row__meta {
  font-size: 11px;
  color: #94a3b8;
  flex-shrink: 0;
}
.fe-row__tag {
  flex-shrink: 0;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 7px;
  border-radius: 10px;
}
.fe-row__tag--warm { background: #fff7ed; color: #c2410c; border: 1px solid #fed7aa; }
.fe-row__tag--cool { background: #eff6ff; color: #1d4ed8; border: 1px solid #bfdbfe; }
.fe-row__tail {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}
.fe-row__badge {
  font-size: 11px;
  color: #64748b;
  background: #f1f5f9;
  padding: 2px 8px;
  border-radius: 10px;
}
.fe-row__actions {
  display: flex;
  opacity: 0.4;
  transition: opacity 0.15s;
}
.fe-row:hover .fe-row__actions { opacity: 1; }
.fe-btn-danger { color: #ef4444 !important; }

/* ── 三级成绩项卡片 ── */
.fe-score-card {
  padding: 14px 16px;
  background: #fafbfc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  transition: border-color 0.15s, box-shadow 0.15s;
}
.fe-score-card:hover {
  border-color: #cbd5e1;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
}
.fe-score-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.fe-score-card__title {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
}
.fe-score-card__actions { display: flex; gap: 2px; }
.fe-score-card__meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 8px;
}
.fe-score-card__star {
  font-size: 12px;
  color: #d97706;
  font-weight: 500;
}
.fe-score-card__star i { margin-right: 2px; }
.fe-score-card__grades {
  font-size: 12px;
  color: #64748b;
  background: #f1f5f9;
  padding: 2px 8px;
  border-radius: 6px;
}
.fe-score-card__standards {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.fe-std-chip {
  font-size: 11px;
  color: #15803d;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
  padding: 3px 8px;
  border-radius: 6px;
  line-height: 1.4;
}

/* ── 空/占位 ── */
.fe-panel-empty,
.fe-panel-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 40px 16px;
  color: #94a3b8;
  font-size: 13px;
  text-align: center;
}
.fe-panel-empty i,
.fe-panel-placeholder i {
  font-size: 28px;
  color: #cbd5e1;
}
.fe-panel-placeholder i { transform: scaleX(-1); }

.fe-form-tip { font-size: 11px; color: #94a3b8; margin-left: 8px; }
.fe-form-item-stack {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
}
.fe-form-tip--block {
  margin: 0;
  line-height: 1.5;
}

/* 成绩项弹窗 Tab */
.fe-item-dialog__tabs >>> .el-tabs__header {
  margin-bottom: 16px;
}
.fe-item-dialog__tabs >>> .el-tabs__item {
  font-size: 13px;
  font-weight: 500;
}
.fe-item-dialog__tabs >>> .el-tab-pane {
  min-height: 280px;
  max-height: 440px;
  overflow-y: auto;
  padding-right: 4px;
}

/* 评价标准 — 卡片列表 */
.fe-standards-panel {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.fe-standards-panel__toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}
.fe-standards-panel__icon {
  flex-shrink: 0;
  font-size: 14px;
  color: #94a3b8;
}
.fe-standards-panel__hint {
  flex: 1;
  min-width: 0;
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
}
.fe-standards-panel__fill-btn {
  flex-shrink: 0;
  border-color: #bfdbfe !important;
  color: #2563eb !important;
  background: #fff !important;
}
.fe-standards-panel__fill-btn:hover,
.fe-standards-panel__fill-btn:focus {
  border-color: #93c5fd !important;
  color: #1d4ed8 !important;
  background: #eff6ff !important;
}
.fe-standard-card {
  padding: 10px 12px 12px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  transition: border-color 0.15s;
}
.fe-standard-card:focus-within {
  border-color: #93c5fd;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.08);
}
.fe-standard-card__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}
.fe-standard-card__badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
  color: #b45309;
  background: #fffbeb;
  border-radius: 6px;
}
.fe-standard-card__head >>> .el-rate {
  height: auto;
  line-height: 1;
}
.fe-standard-card__head >>> .el-rate__icon {
  font-size: 16px;
  margin-right: 2px;
}
.fe-standard-card__input >>> .el-textarea__inner {
  padding: 8px 10px;
  font-size: 13px;
  line-height: 1.6;
  border-radius: 8px;
  resize: vertical;
  min-height: 56px !important;
}
.fe-standard-card__input >>> .el-textarea__inner::placeholder {
  color: #94a3b8;
  font-size: 12px;
  line-height: 1.6;
}
</style>

<style>
/* 关联点评项级联面板（挂载到 body，不可 scoped） */
.fe-comment-cascader.el-cascader__dropdown {
  min-width: 420px;
}
.fe-comment-cascader .el-cascader-panel {
  min-height: 220px;
}
.fe-comment-cascader .el-cascader-menu {
  min-width: 200px;
}
</style>
